import { CheckCircle, Edit, MessageSquare, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ModerationQueueItem } from "@/types/api";

interface AdminReviewListProps {
  providers: ModerationQueueItem[];
  isProcessing: boolean;
  handleEditListing: (id: string) => void;
  handleStatusChange: (id: string, status: "approved" | "rejected") => void;
}

const statusBadgeClassName = (status: ModerationQueueItem["status"]) => {
  switch (status) {
    case "approved":
      return "border-whatsapp/20 bg-whatsapp-light text-whatsapp";
    case "rejected":
      return "border-red-200 bg-red-50 text-red-700";
    default:
      return "border-amber-200 bg-amber-50 text-amber-700";
  }
};

const AdminReviewList = ({
  providers,
  isProcessing,
  handleEditListing,
  handleStatusChange,
}: AdminReviewListProps) => {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-900">Moderation queue</h2>
        <p className="mt-1 text-sm text-gray-500">
          Context, contact details, and review actions stay together so operators can decide without drilling in first.
        </p>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-gray-200 hover:bg-transparent">
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Listing</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Owner</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Coverage</TableHead>
            <TableHead className="bg-white text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Submitted</TableHead>
            <TableHead className="bg-white text-right text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {providers.map((provider) => (
            <TableRow key={provider.listingId} className="border-gray-200 hover:bg-gray-50">
              <TableCell className="align-top">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">{provider.name}</span>
                    <Badge variant="outline" className={statusBadgeClassName(provider.status)}>
                      {provider.status}
                    </Badge>
                    {provider.flags.isUnassigned ? (
                      <Badge variant="outline" className="border-gray-200 bg-gray-100 text-gray-700">
                        Unassigned
                      </Badge>
                    ) : null}
                  </div>
                  <p className="line-clamp-2 max-w-md text-sm text-gray-500">{provider.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {(provider.categories || [provider.category]).map((category) => (
                      <Badge key={category} variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TableCell>

              <TableCell className="align-top">
                <div className="space-y-1 text-sm">
                  <div className="font-medium text-gray-900">
                    {provider.owner.firstName || provider.owner.lastName
                      ? `${provider.owner.firstName ?? ""} ${provider.owner.lastName ?? ""}`.trim()
                      : "No owner profile"}
                  </div>
                  <div className="text-gray-500">{provider.owner.email || provider.profiles?.email || "No email"}</div>
                  {(provider.profiles?.whatsapp_number || provider.whatsappNumber) ? (
                    <div className="flex items-center gap-1 text-gray-500">
                      <MessageSquare className="h-3.5 w-3.5" />
                      {provider.profiles?.whatsapp_number || provider.whatsappNumber}
                    </div>
                  ) : null}
                </div>
              </TableCell>

              <TableCell className="align-top">
                {provider.suburbs && provider.suburbs.length > 0 ? (
                  <div className="flex max-w-xs flex-wrap gap-1">
                    {provider.suburbs.slice(0, 4).map((suburb) => (
                      <Badge
                        key={suburb}
                        variant="outline"
                        className="border-gray-200 bg-gray-100 text-gray-600"
                      >
                        {suburb}
                      </Badge>
                    ))}
                    {provider.suburbs.length > 4 ? (
                      <Badge variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                        +{provider.suburbs.length - 4} more
                      </Badge>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">No areas listed</span>
                )}
              </TableCell>

              <TableCell className="align-top text-sm text-gray-500">
                {new Date(provider.submittedAt).toLocaleDateString()}
              </TableCell>

              <TableCell className="align-top text-right">
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
                    onClick={() => handleEditListing(provider.listingId)}
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark"
                    onClick={() => handleStatusChange(provider.listingId, "approved")}
                    disabled={isProcessing}
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="rounded-lg"
                    onClick={() => handleStatusChange(provider.listingId, "rejected")}
                    disabled={isProcessing}
                  >
                    <XCircle className="h-4 w-4" />
                    Reject
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default AdminReviewList;
